import { itemSearchText } from "../domain/items/search-fields";
import {studyRows} from './study-query';
import {sopSearchRows} from './sop-query';
import { snippet } from "../domain/content/search-text";
import { visibleContent } from "./content-service";
import {
  contentKinds,
  contentPath,
  contentTitle,
} from "../domain/content/model";
import type { NookDatabase } from "../infrastructure/database/database";
import { itemPath, type Kind } from "../domain/items/model";
export const normalize = (text: string) =>
  text.normalize("NFKC").toLocaleLowerCase("und");
export async function searchLocal(db: NookDatabase, query: string) {
  const needle = normalize(query.trim());
  if (!needle) return [];
  return db.transaction("r", db.tables, async () => {
    const projects = await db.projects.toArray(),
      names = new Map(projects.filter(p=>!p.deletedAt).map((p) => [p.id, p.title])),
      attachments = new Map(
        (await db.attachments.toArray()).map((a) => [a.id, a.name]),
      ),
      itemDrafts = (await db.itemDrafts.toArray()).filter((d) => !d.deletedAt);
    const result: {
        id: string;
        kind: string;
        title: string;
        summary: string;
        path: string;
        archived: boolean;
        date?: string;
        tags?: string[];
      }[] = [],
      seen = new Set<string>();
    const matches = (fields: string, ids: string[]) =>
      normalize(
        fields + " " + ids.map((id) => attachments.get(id) ?? "").join(" "),
      ).includes(needle);
    for (const kind of ["task", "event", "project"] as Kind[]) {
      const rows =
        kind === "task"
          ? await db.tasks.toArray()
          : kind === "event"
            ? await db.events.toArray()
            : projects;
      for (const item of rows) {
        seen.add(item.id);
        if (item.deletedAt) continue;
        const d = itemDrafts
            .filter(
              (d) =>
                d.kind === kind &&
                d.targetId === item.id &&
                d.baseRevision === item.revision,
            )
            .sort(
              (a, b) =>
                b.updatedAt.localeCompare(a.updatedAt) ||
                a.id.localeCompare(b.id),
            )[0],
          visible = d?.payload ?? item;
        if (
          matches(
            [
              itemSearchText(visible),
              "projectId" in visible ? names.get(visible.projectId ?? "") : "",
              "state" in visible ? visible.state : "",
            ].join(" "),
            visible.attachmentIds,
          )
        )
          result.push({
            id: item.id,
            kind: kind + (d ? " 草稿" : ""),
            title: visible.title || "未命名草稿",
            summary: snippet(visible.body, query),
            path:
              itemPath(kind, item.id) +
              (d
                ? `/edit?draft=${d.id}${d.sourceCaptureId ? "&capture=" + d.sourceCaptureId : ""}`
                : ""),
            archived: !!item.archivedAt,
            date: d?.updatedAt.slice(0,10) ?? item.updatedAt.slice(0, 10),
          });
      }
    }
    const captures = await db.captures.toArray(),
      drafts = await db.drafts.toArray();
    for (const c of captures) {
      seen.add(c.id);
      if (c.deletedAt) continue;
      const d = drafts
        .filter(
          (d) =>
            d.captureId === c.id &&
            !d.deletedAt &&
            d.state === "active" &&
            d.baseRevision === c.revision,
        )
        .sort(
          (a, b) =>
            b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id),
        )[0];
      const body = d?.body ?? c.body;
      if (matches(body, d?.attachmentIds ?? c.attachmentIds))
        result.push({
          id: c.id,
          kind: d ? "捕捉草稿" : "捕捉",
          title: body.split("\n")[0].slice(0, 80) || "图片记录",
          summary: body.slice(0, 120),
          path: d
            ? `/captures/${c.id}/edit?draft=${d.id}`
            : `/captures/${c.id}`,
          archived: !!c.archivedAt,
          date: c.updatedAt.slice(0, 10),
        });
    }
    for (const d of drafts.filter((d) => !d.deletedAt && d.state === "active"))
      if (!seen.has(d.captureId)) {
        seen.add(d.captureId);
        if (matches(d.body, d.attachmentIds))
          result.push({
            id: d.captureId,
            kind: "捕捉草稿",
            title: d.body.slice(0, 80),
            summary: d.body.slice(0, 120),
            path: `/captures/${d.captureId}/edit?draft=${d.id}`,
            archived: false,
            date: d.updatedAt.slice(0, 10),
          });
      }
    for (const d of itemDrafts)
      if (!seen.has(d.targetId)) {
        seen.add(d.targetId);
        if (matches(itemSearchText(d.payload), d.payload.attachmentIds))
          result.push({
            id: d.targetId,
            kind: `${d.kind} 草稿`,
            title: d.payload.title || "未命名草稿",
            summary: d.payload.body.slice(0, 120),
            path: `${itemPath(d.kind, d.targetId)}/edit?draft=${d.id}${d.sourceCaptureId ? "&capture=" + d.sourceCaptureId : ""}`,
            archived: false,
            date: d.updatedAt.slice(0, 10),
          });
      }
    for (const d of itemDrafts) {
      const current = await db.table(d.kind==='task'?'tasks':d.kind==='event'?'events':'projects').get(d.targetId);
      if (!current || current.deletedAt || d.baseRevision===current.revision) continue;
      if(matches(itemSearchText(d.payload),d.payload.attachmentIds))result.push({id:d.id,kind:d.kind+' 草稿 · 冲突分支',title:d.payload.title||'未命名草稿',summary:snippet(d.payload.body,query),path:itemPath(d.kind,d.targetId)+'/edit?draft='+d.id+(d.sourceCaptureId?'&capture='+d.sourceCaptureId:''),archived:!!current.archivedAt,date:d.updatedAt.slice(0,10)});
    }
    for (const d of drafts.filter(d=>!d.deletedAt)) {
      const current=captures.find(c=>c.id===d.captureId);
      if(!current||current.deletedAt||d.state==='active'&&d.baseRevision===current.revision)continue;
      if(matches(d.body,d.attachmentIds))result.push({id:d.id,kind:'捕捉草稿 · 冲突分支',title:d.body.split('\n')[0].slice(0,80)||'图片草稿',summary:snippet(d.body,query),path:'/captures/'+d.captureId+'/edit?draft='+d.id,archived:!!current.archivedAt,date:d.updatedAt.slice(0,10)});
    }
    const excluded =
        (await db.settings.get("diarySearch"))?.value === "excluded",
      hidden = (await db.settings.get("diarySummary"))?.value === "hidden";
    for (const kind of contentKinds) {
      if (kind === "diary" && excluded) continue;
      const groups = new Map<
        string,
        Awaited<ReturnType<typeof visibleContent>>
      >();
      for (const entry of await visibleContent(db, kind)) {
        const group = groups.get(entry.item.id) ?? [];
        group.push(entry);
        groups.set(entry.item.id, group);
      }
      for (const [id, entries] of groups) {
        const main = entries.find((x) => !x.conflict) ?? entries[0],
          v = main.item;
        const verified = (x: typeof main) =>
          matches(
            [x.item.title,x.item.body,"choice" in x.item ? x.item.choice+" "+x.item.reason : "url" in x.item ? x.item.url+" "+x.item.excerpt+" "+x.item.summary : "",x.tags.join(" ")].join(" "),
            x.item.attachmentIds,
          );
        const mainMatch = verified(main), conflicts = entries.filter((x) => x.conflict && verified(x));
        if (!mainMatch && !conflicts.length) continue;
        const privacy = kind === "diary" && hidden;
        result.push({
          id,
          kind:
            kind +
            (main.draft ? " 草稿" : "") +
            (conflicts.length ? " · 有冲突草稿匹配" : ""),
          title: privacy ? "日记" : contentTitle(v),
          summary: privacy
            ? "摘要已隐藏"
            : mainMatch
              ? snippet(
                  [
                    v.body,
                    "reason" in v
                      ? v.choice + " " + v.reason
                      : "url" in v
                        ? v.excerpt + " " + v.summary + " " + v.url
                        : "",
                  ].join("\n"),
                  query,
                )
              : "冲突草稿有匹配；打开记录比较双方，尚未覆盖当前版本。",
          path:
            contentPath(kind, id) +
            (main.draft ? "/edit?draft=" + main.draft.id : ""),
          archived: !!v.archivedAt,
          date: "diaryDate" in v ? v.diaryDate : v.updatedAt.slice(0, 10),
          tags: main.tags,
        });
      }
    }
    for(const row of [...await sopSearchRows(db),...await studyRows(db)])if(normalize(row.body).includes(needle))result.push({id:row.id,kind:row.kind,title:row.title,summary:snippet(row.body,query),path:row.path,archived:row.archived,date:row.date});
    return result;
  });
}
